import { z } from 'zod'

import type {
  CommonResponseCollectionStudentLearningWrongStepKeyValueResponse,
  CommonResponseStudentLearningConceptStepResponse,
  StudentLearningConceptStepResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  type ConceptLearningStep,
  ConceptLearningStepSchema,
  type LearnedLecture,
  LearnedLectureSchema,
  type WrongLearningStep,
  WrongLearningStepSchema,
} from './schemas'

// 강의는 Lecture 와 같은 생성 타입(LectureResponse)을 품어 같은 드리프트를 그대로 안는다 —
// videoLengthSeconds 를 스펙은 Duration 객체로 그리지만 실제로는 초 단위 숫자다. 스키마가 실제
// 모양을 검증하므로 입력은 생성 타입으로 받되 이 한 줄에서만 unknown 을 거친다.
// 2026-09-19 생성 타입 LectureResponse 와 @entities/Lecture 의 같은 주석으로 확인.
const asWire = (raw: StudentLearningConceptStepResponse | undefined): unknown => raw

// 아직 듣지 않은 강의는 studentLecture 를 키째 빼고 보내기도 한다. 검증은 둘 다 받고, null 로 맞추는 일은 검증 뒤에 한다
// (transform 에 두면 운영 lenient 에서 raw 가 넘어올 때 빠진다).
const _LearnedLectureWireSchema = LearnedLectureSchema.extend({
  studentLecture: LearnedLectureSchema.shape.studentLecture.optional(),
})

const _ConceptLearningStepWireSchema = ConceptLearningStepSchema.extend({
  lectures: z.array(_LearnedLectureWireSchema),
  prevLectures: z.array(_LearnedLectureWireSchema),
})

const toLearnedLecture = (lecture: z.output<typeof _LearnedLectureWireSchema>): LearnedLecture => ({
  ...lecture,
  studentLecture: lecture.studentLecture ?? null,
})

export const adaptConceptLearningStep = (
  raw: CommonResponseStudentLearningConceptStepResponse['data'],
): ConceptLearningStep => {
  const step = parseApiResponse(
    _ConceptLearningStepWireSchema,
    asWire(raw),
    'StudentLearningProcess',
  )
  return (
    step && {
      ...step,
      lectures: (step.lectures ?? []).map(toLearnedLecture),
      prevLectures: (step.prevLectures ?? []).map(toLearnedLecture),
    }
  )
}

// 오답학습 단계의 문제는 ProblemResponse 이름 충돌로 생성 타입이 잡탕이다(문제 본체·학습지 필터 추천·그룹
// 성취도 중첩이 한 칸을 덮어쓴다). 실제로는 문제 본체 V1 이 온다. 입력은 생성 타입으로 받되 이 한 줄에서만
// unknown 을 거친다. 2026-09-30 백엔드 develop 의 studentLearning/controller/response/
// StudentLearningWrongStepKeyValueResponse.kt 로 확인.
const asWrongStepWire = (
  raw: CommonResponseCollectionStudentLearningWrongStepKeyValueResponse['data'],
): unknown => raw

export const adaptWrongLearningSteps = (
  raw: CommonResponseCollectionStudentLearningWrongStepKeyValueResponse['data'],
): WrongLearningStep[] =>
  parseApiResponse(z.array(WrongLearningStepSchema), asWrongStepWire(raw), 'StudentLearningProcess')
